import {
  Box,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
} from "@mui/material";
import CachedIcon from "@mui/icons-material/Cached";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import { DatePicker } from "@mui/x-date-pickers";
import SelectMultipleEntity from "components/SelectMultipleEntity";
import { isValid } from "date-fns";
import useGlobalContext from "hooks/globalData";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import { Link } from "react-router-dom";
import { ListRequestOfBaseFilter } from "services/KnowfullyAdminClient";
import persistanceStore from "stores/persistanceStore";
import { standardDateFormat } from "utils/dateUtils";

type UserSkusTableProps = {
  userId: string;
};
const UserSkusTableTitle = ({ userId }: UserSkusTableProps) => {
  const { formatMessage: t } = useIntl();

  const { userSkuService, skuService, bulkEnrollmentUrl } = useGlobalContext();

  const queryClient = useQueryClient();
  const searchTerm = persistanceStore.useState(
    (s) => s.userSkusFilterSearchTerm
  );
  const startDateSearch = persistanceStore.useState(
    (s) => s.userSkusFilterStartDate
  );
  const endDateSearch = persistanceStore.useState(
    (s) => s.userSkusFilterEndDate
  );
  const skuFormatIds = persistanceStore.useState(
    (s) => s.userSkusFilterSkuFormatIds
  );
  const userSkuStateIds = persistanceStore.useState(
    (s) => s.userSkusFilterStateIds
  );

  const fetchedSkuFormats = useQuery(["skuFormats"], () =>
    skuService.getSkuFormats(
      ListRequestOfBaseFilter.fromJS({
        sortField: "name",
      })
    )
  );

  const fetchedUserSkuStates = useQuery(["userSkuStates"], () =>
    userSkuService.getUserSkuStates(ListRequestOfBaseFilter.fromJS({}))
  );

  const refresh = () => {
    queryClient.invalidateQueries(["userSkus", userId]);
  };

  return (
    <Box mb={2}>
      <Grid
        container
        spacing={2}
        justifyItems="center"
        justifyContent="space-between"
      >
        <Grid item xs={11} sm={12} md={2}>
          <TextField
            fullWidth
            variant="outlined"
            value={searchTerm ?? ""}
            placeholder={t({ id: "searchPlaceholder" })}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() =>
                      persistanceStore.update((s) => {
                        s.userSkusFilterSearchTerm = "";
                      })
                    }
                  >
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            onChange={(e) => {
              persistanceStore.update((s) => {
                s.userSkusFilterSearchTerm = e.target.value;
              });
            }}
          />
        </Grid>
        <Grid item xs={11} sm={12} md={2}>
          <DatePicker
            label={t({ id: "startDateLabel" })}
            value={startDateSearch || null}
            onChange={(date: any) => {
              if (isValid(date) || date == null)
                persistanceStore.update((s) => {
                  s.userSkusFilterStartDate = date;
                });
            }}
            format={standardDateFormat}
            slotProps={{
              textField: { fullWidth: true, variant: "standard" },
              actionBar: {
                actions: ["clear", "cancel"],
              },
            }}
          />
        </Grid>
        <Grid item xs={11} sm={12} md={2}>
          <DatePicker
            label={t({ id: "endDateLabel" })}
            value={endDateSearch || null}
            onChange={(date: any) => {
              if (isValid(date) || date == null)
                persistanceStore.update((s) => {
                  s.userSkusFilterEndDate = date;
                });
            }}
            format={standardDateFormat}
            slotProps={{
              textField: { fullWidth: true, variant: "standard" },
              actionBar: {
                actions: ["clear", "cancel"],
              },
            }}
          />
        </Grid>
        <Grid item xs={11} sm={12} md={2}>
          <SelectMultipleEntity
            values={skuFormatIds}
            fetchedData={fetchedSkuFormats}
            labelField="name"
            label={t({ id: "skuFormatLabel" })}
            onChange={(ids) =>
              persistanceStore.update((s) => {
                s.userSkusFilterSkuFormatIds = ids;
              })
            }
          />
        </Grid>
        <Grid item xs={11} sm={12} md={2}>
          <SelectMultipleEntity
            values={userSkuStateIds}
            fetchedData={fetchedUserSkuStates}
            labelField="name"
            label={t({ id: "statusLabel" })}
            onChange={(ids) =>
              persistanceStore.update((s) => {
                s.userSkusFilterStateIds = ids;
              })
            }
          />
        </Grid>
        <Grid item xs={11} sm={12} md={1}>
          <Link
            to={bulkEnrollmentUrl(userId)}
            style={{ textDecoration: "none" }}
          >
            <Button>
              <T id="addSku" />
            </Button>
          </Link>
        </Grid>
        <Grid item xs={11} sm={12} md={1}>
          <Tooltip
            title={t({
              id: "refresh",
            })}
          >
            <IconButton onClick={refresh}>
              <CachedIcon />
            </IconButton>
          </Tooltip>
        </Grid>
      </Grid>
    </Box>
  );
};

export default UserSkusTableTitle;
